import { useState } from 'react';
import { Loader2, Zap } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Demo-mode banner — shown when /auth?demo=1 is present.
 * Calls demo-otp-bypass to sign the configured demo user in instantly,
 * skipping email delivery so on-stage demos are reliable.
 */
export function DemoBypassBanner({ onSuccess }: { onSuccess?: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('demo-otp-bypass', { body: {} });
      if (invokeErr) throw invokeErr;
      if (!data?.access_token || !data?.refresh_token) throw new Error('No session returned');
      const { error: sessErr } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      if (sessErr) throw sessErr;
      onSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Demo bypass failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-200 text-amber-900 text-xs font-semibold">
          <Zap className="w-3 h-3" /> DEMO MODE
        </span>
        <span className="text-sm text-amber-900">Skip OTP and sign in as the configured demo user.</span>
      </div>
      <button
        onClick={handleClick}
        disabled={loading}
        className="inline-flex items-center gap-2 px-4 py-2 bg-amber-900 text-white text-sm font-medium hover:bg-amber-800 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {loading ? 'Signing in…' : 'Sign in as demo user'}
      </button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}